import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { ArrowLeft, Camera, Save, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { resolveAvatarUrl } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, token, updateUser } = useAuth();
  const { isDarkMode } = useTheme();
  const fileInputRef = useRef(null);
  
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [statusMessage, setStatusMessage] = useState(user?.status_message || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || null);
  const [avatarPreview, setAvatarPreview] = useState(resolveAvatarUrl(user?.avatar_url) || null);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API}/users/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
    setChangingPassword(false);
  };

  const handleAvatarSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);

    // Upload the file
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const { url } = uploadResponse.data;
      setAvatarUrl(url);
      
      // Auto-save avatar to profile immediately
      const profileResponse = await axios.put(`${API}/users/profile`, {
        display_name: displayName.trim() || user?.display_name,
        avatar_url: url
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      updateUser(profileResponse.data);
      toast.success('Photo saved');
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      toast.error('Failed to upload photo');
      setAvatarPreview(resolveAvatarUrl(user?.avatar_url) || null);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const removeAvatar = () => {
    setAvatarUrl(null);
    setAvatarPreview(null);
  };

  const handleSave = async () => {
    if (!displayName.trim()) {
      toast.error('Display name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(`${API}/users/profile`, {
        display_name: displayName.trim(),
        bio: bio.trim(),
        status_message: statusMessage.trim(),
        avatar_url: avatarUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      updateUser(response.data);
      toast.success('Profile updated successfully');
      navigate('/settings');
    } catch (error) {
      toast.error('Failed to update profile');
    }
    setSaving(false);
  };

  // Status message presets (like WhatsApp)
  const statusPresets = [
    "Available",
    "Busy",
    "At work",
    "In a meeting",
    "At the gym",
    "Sleeping",
    "Can't talk, WhatsApp only",
    "Battery about to die"
  ];

  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode ? 'bg-gray-950' : 'bg-background-DEFAULT'}`} data-testid="edit-profile-page">
      <div className={`p-6 border-b ${isDarkMode ? 'border-gray-800' : 'border-border-DEFAULT'}`}>
        <div className="chat-container flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-btn"
              onClick={() => navigate('/settings')}
              className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${
                isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-secondary-DEFAULT'
              }`}
            >
              <ArrowLeft size={20} className={isDarkMode ? 'text-white' : 'text-text-primary'} />
            </button>
            <h1 className={`font-heading text-2xl ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>Edit Profile</h1>
          </div>
          <button
            data-testid="save-btn"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide p-6">
        <div className="chat-container max-w-2xl mx-auto space-y-6">
          {/* Profile Picture */}
          <div className="flex justify-center">
            <div className="relative">
              {avatarPreview ? (
                <div className="relative">
                  <img
                    src={avatarPreview}
                    alt="Profile"
                    className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                  <button
                    onClick={removeAvatar}
                    className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                    data-testid="remove-avatar-btn"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium text-4xl shadow-lg">
                  {displayName?.charAt(0).toUpperCase() || user?.display_name?.charAt(0).toUpperCase()}
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarSelect}
                className="hidden"
                data-testid="avatar-input"
              />
              
              <button
                data-testid="change-avatar-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
                className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-white border-2 border-[#D4AF37] flex items-center justify-center hover:bg-gray-50 transition-colors shadow-md disabled:opacity-50"
              >
                {uploadingAvatar ? (
                  <Loader2 size={18} className="text-[#D4AF37] animate-spin" />
                ) : (
                  <Camera size={18} className="text-[#D4AF37]" />
                )}
              </button>
            </div>
          </div>

          {/* Form */}
          <div className={`rounded-3xl p-6 shadow-card border space-y-6 ${
            isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-border-DEFAULT'
          }`}>
            {/* Display Name */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>
                Display Name
              </label>
              <input
                data-testid="display-name-input"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your display name"
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' 
                    : 'bg-white border-border-DEFAULT text-text-primary'
                }`}
              />
            </div>

            {/* Username (read-only) */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>
                Username
              </label>
              <input
                type="text"
                value={`@${user?.username}`}
                disabled
                className={`w-full px-4 py-3 border rounded-xl ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 text-gray-500' 
                    : 'bg-gray-50 border-border-DEFAULT text-text-tertiary'
                }`}
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-text-tertiary'}`}>Username cannot be changed</p>
            </div>

            {/* Status Message */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>
                Status
              </label>
              <input
                data-testid="status-input"
                type="text"
                value={statusMessage}
                onChange={(e) => setStatusMessage(e.target.value)}
                placeholder="What's on your mind?"
                maxLength={100}
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' 
                    : 'bg-white border-border-DEFAULT text-text-primary'
                }`}
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-text-tertiary'}`}>{statusMessage.length}/100 characters</p>
              
              {/* Status Presets */}
              <div className="flex flex-wrap gap-2 mt-3">
                {statusPresets.map((preset, index) => (
                  <button
                    key={index}
                    onClick={() => setStatusMessage(preset)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      statusMessage === preset
                        ? 'bg-[#D4AF37] text-white'
                        : isDarkMode
                          ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>
                Bio
              </label>
              <textarea
                data-testid="bio-input"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell us about yourself..."
                rows={4}
                maxLength={200}
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] transition-colors resize-none ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' 
                    : 'bg-white border-border-DEFAULT text-text-primary'
                }`}
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-text-tertiary'}`}>{bio.length}/200 characters</p>
            </div>

            {/* Change Password Section */}
            <div className={`p-4 rounded-xl border ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
              <h3 className={`text-sm font-medium mb-4 ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>
                Change Password
              </h3>
              <div className="space-y-3">
                <input
                  data-testid="current-password-input"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Current password"
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' 
                      : 'bg-white border-border-DEFAULT text-text-primary'
                  }`}
                />
                <input
                  data-testid="new-password-input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password (min 6 characters)"
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' 
                      : 'bg-white border-border-DEFAULT text-text-primary'
                  }`}
                />
                <input
                  data-testid="confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' 
                      : 'bg-white border-border-DEFAULT text-text-primary'
                  }`}
                />
                <button
                  data-testid="change-password-btn"
                  onClick={handleChangePassword}
                  disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                  className="w-full px-4 py-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 font-medium"
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
